import { NoteContextType } from '@components/common/TagBadge/TagBadge.component'
import { isProviderSeatRole } from '@constants/Providers'
import type { IFavoritedUser } from '@dataTypes/user-favorite.type'
import type { INote } from '@dataTypes/note.type'
import type { NoteFilterContextLookup } from '@utils/noteFilter.util'
import { getFullName } from '@utils/fullname.util'
import { format } from 'date-fns'
import { BUILT_IN_KEY_TO_NAME_CATEGORY } from './noteEditor.constants'
import type { CustomTemplate, NoteSidebarFilters } from './noteEditor.types'

export const isGeneralPatientContextType = (
  contextType: string | null | undefined,
): boolean => {
  const ctx = String(contextType ?? '').toLowerCase()
  return ctx === NoteContextType.PATIENT || ctx === 'patient'
}

export const getNoteContextItemId = (note: INote): string => {
  const direct = note.context_id?.trim()
  if (direct) return direct
  const details = note.context_details ?? {}
  const appointmentId =
    typeof details.appointment_id === 'string'
      ? details.appointment_id.trim()
      : ''
  if (appointmentId) return appointmentId
  const chartId =
    typeof details.chart_id === 'string' ? details.chart_id.trim() : ''
  if (chartId) return chartId
  const tpId = typeof details.tp_id === 'string' ? details.tp_id.trim() : ''
  return tpId
}

export const applySidebarFiltersToNotes = (
  notes: INote[],
  filters: NoteSidebarFilters,
  sidebarSearchQuery: string,
): INote[] => {
  let filtered = [...notes]
  if (sidebarSearchQuery) {
    filtered = filtered.filter((n) => {
      const body = n.body ?? ''
      return `${n.title} ${body}`
        .toLowerCase()
        .includes(sidebarSearchQuery.toLowerCase())
    })
  }
  if (filters.filterBy === 'title') {
    filtered = filtered.filter((n) =>
      n.title.toLowerCase().includes(sidebarSearchQuery.toLowerCase()),
    )
  } else if (filters.filterBy === 'note') {
    filtered = filtered.filter((n) => {
      const body = n.body ?? ''
      return body.toLowerCase().includes(sidebarSearchQuery.toLowerCase())
    })
  }
  if (filters.dateFrom) {
    filtered = filtered.filter((n) => {
      const noteDate = new Date(n.date_time_created_at ?? '')
      const fromDate = new Date(filters.dateFrom)
      return noteDate >= fromDate
    })
  }
  if (filters.dateTo) {
    filtered = filtered.filter((n) => {
      const noteDate = new Date(n.date_time_created_at ?? '')
      const toDate = new Date(filters.dateTo)
      toDate.setHours(23, 59, 59, 999)
      return noteDate <= toDate
    })
  }
  if (filters.category) {
    filtered = filtered.filter((n) => {
      const noteContextType = n.context_type ?? ''
      return noteContextType.toString() === filters.category
    })
  }
  if (filters.item) {
    filtered = filtered.filter((n) => getNoteContextItemId(n) === filters.item)
  }
  return filtered
}

/** Doctor speaker label for notes transcription; only when the logged-in user is a provider. */
export const buildDoctorDisplayName = (
  loggedInUser: IFavoritedUser | null,
): string => {
  if (
    loggedInUser &&
    isProviderSeatRole(loggedInUser.provider_profile?.work_role ?? undefined)
  ) {
    const fullName = getFullName(loggedInUser).trim()
    return fullName ? `Dr. ${fullName}` : ''
  }
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const user = JSON.parse(raw) as {
      first_name?: string
      last_name?: string
      provider_id?: string
    }
    if (!user.provider_id?.trim()) return ''
    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(' ')
      .trim()
    return fullName ? `Dr. ${fullName}` : ''
  } catch {
    return ''
  }
}

const getStringValue = (
  details: Record<string, unknown> | null | undefined,
  key: string,
): string | null => {
  if (!details) return null
  const value = details[key]
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : null
}

export const formatAppointmentHeaderContextName = (
  note: INote | null,
  patientName: string,
): string | null => {
  if (!note) return null
  const contextType = String(note.context_type ?? '').toLowerCase()
  if (contextType !== NoteContextType.APPOINTMENT) return null
  const details = note.context_details as Record<string, unknown> | null
  const apptDate =
    getStringValue(details, 'appt_date') ??
    (note.appointment?.appt_date?.trim() || null)
  const apptStart =
    getStringValue(details, 'appt_start') ??
    (note.appointment?.appt_start?.trim() || null)
  if (!apptDate) return patientName
  const parsedDate = new Date(apptDate)
  if (Number.isNaN(parsedDate.getTime())) return patientName
  const datePart = format(parsedDate, 'MM/dd/yy')
  const timePart = apptStart ? apptStart.slice(0, 5) : ''
  return timePart
    ? `${patientName} (${datePart} ${timePart})`
    : `${patientName} (${datePart})`
}

export const formatAppointmentContextNameFromLookup = (
  contextId: string,
  lookup: NoteFilterContextLookup,
  patientName: string,
): string | null => {
  const appointment = lookup.appointments?.find(
    (item) => item.appointment_id === contextId,
  )
  if (!appointment) return null
  const startDateTime =
    appointment.appointment_start_date ??
    appointment.appointment_start_date_time
  if (startDateTime) {
    const parsed = new Date(startDateTime)
    if (!Number.isNaN(parsed.getTime())) {
      return `${patientName} (${format(parsed, 'MM/dd/yy HH:mm')})`
    }
  }
  if (!appointment.appt_date) return patientName
  const date = new Date(appointment.appt_date)
  if (Number.isNaN(date.getTime())) return patientName
  const datePart = format(date, 'MM/dd/yy')
  const timePart = appointment.appt_start?.slice(0, 5) ?? ''
  return timePart
    ? `${patientName} (${datePart} ${timePart})`
    : `${patientName} (${datePart})`
}

export const isNoteContentEmpty = (
  content: string | null | undefined,
): boolean => {
  if (content == null) return true
  const plainText = content.replace(/<[^>]*>/g, '').trim()
  return plainText === '' || plainText === '<br>' || plainText === '<p><br></p>'
}

export const findMatchingCustomTemplate = (
  templateKey: string,
  customTemplates: readonly CustomTemplate[],
): CustomTemplate | undefined => {
  const byId = customTemplates.find((template) => template.id === templateKey)
  if (byId) return byId
  const nameCategory = BUILT_IN_KEY_TO_NAME_CATEGORY[templateKey]
  if (!nameCategory) return undefined
  return customTemplates.find(
    (template) =>
      template.name === nameCategory.name &&
      template.category === nameCategory.category,
  )
}
